<template>
  <div class="content">
    <div class="pathway-detail-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-pathway-detail">
    <div v-if="loading" class="glad-loading">
      <span class="glad-spinner" aria-label="Loading pathway"></span>
    </div>

    <template v-else-if="pathway">
      <!-- Back nav -->
      <nav class="glad-breadcrumb">
        <router-link to="/glad/pathways" class="glad-breadcrumb__link">
          ← My Pathways
        </router-link>
      </nav>

      <!-- Pathway header -->
      <header class="glad-pathway-header">
        <div class="glad-pathway-header__text">
          <span v-if="pathway.track" class="glad-eyebrow">{{ formatTrack(pathway.track) }}</span>
          <h1 class="glad-pathway-header__title">{{ pathway.title }}</h1>
          <p v-if="pathway.subtitle" class="glad-pathway-header__sub">{{ pathway.subtitle }}</p>

          <div class="glad-pathway-header__meta">
            <span class="glad-status-pill" :class="statusClass(pathway.pathway_status)">
              {{ statusLabel(pathway.pathway_status) }}
            </span>
            <span class="glad-pathway-header__count">
              {{ pathway.modules ? pathway.modules.length : 0 }} modules
            </span>
          </div>

          <button
            v-if="pathway.pathway_status === 'not_enrolled'"
            class="glad-btn"
            :disabled="enrolling"
            @click="enroll">
            {{ enrolling ? 'Enrolling…' : 'Enroll in this pathway' }}
          </button>
        </div>

        <div v-if="pathway.pathway_status !== 'not_enrolled'" class="glad-pathway-header__progress">
          <svg class="glad-circle-progress" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="34" fill="none" stroke="#f3f2ee" stroke-width="8"/>
            <circle
              cx="40" cy="40" r="34"
              fill="none"
              stroke="#0f1115"
              stroke-width="8"
              stroke-linecap="round"
              :stroke-dasharray="circleCircumference"
              :stroke-dashoffset="circleOffset"
              transform="rotate(-90 40 40)"/>
          </svg>
          <span class="glad-circle-progress__pct">{{ overallPct }}%</span>
        </div>
      </header>

      <p v-if="pathway.description" class="glad-pathway-description">{{ pathway.description }}</p>

      <!-- Module list -->
      <section class="glad-modules">
        <h2 class="glad-modules__heading">Modules</h2>

        <div class="glad-module-list">
          <div
            v-for="(mod, idx) in sortedModules"
            :key="mod.id"
            class="glad-module"
            :class="{
              'glad-module--completed': mod.module_status === 'completed',
              'glad-module--active':    mod.module_status === 'in_progress',
              'glad-module--locked':    mod.module_status === 'locked',
            }">

            <div class="glad-module__number">
              <template v-if="mod.module_status === 'completed'">
                <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20" aria-hidden="true">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                </svg>
              </template>
              <template v-else-if="mod.module_status === 'locked'">
                <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" aria-hidden="true">
                  <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd" />
                </svg>
              </template>
              <template v-else>
                <span>{{ idx + 1 }}</span>
              </template>
            </div>

            <div class="glad-module__content">
              <h3 class="glad-module__title">{{ mod.course_name || 'Module ' + (idx + 1) }}</h3>
              <span class="glad-module__status">{{ moduleStatusLabel(mod.module_status) }}</span>

              <p v-if="mod.module_status === 'locked'" class="glad-module__lock-hint">
                Complete the previous module to unlock.
              </p>
            </div>

            <div class="glad-module__action">
              <router-link
                v-if="mod.module_status === 'in_progress'"
                :to="'/glad/pathways/' + pathway.id + '/modules/' + mod.id"
                class="glad-btn glad-btn--sm">
                Continue
              </router-link>
              <router-link
                v-else-if="mod.module_status === 'completed'"
                :to="'/glad/pathways/' + pathway.id + '/modules/' + mod.id"
                class="glad-btn glad-btn--sm glad-btn--ghost">
                Review
              </router-link>
            </div>
          </div>
        </div>
      </section>
    </template>

    <div v-else class="glad-empty">
      <p>Pathway not found.</p>
    </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PathwayDetail',
  data() {
    return {
      loading: true,
      enrolling: false,
      pathway: null,
    };
  },
  computed: {
    sortedModules() {
      if (!this.pathway?.modules) return [];
      return [...this.pathway.modules].sort((a, b) => a.sequence_order - b.sequence_order);
    },
    overallPct() {
      const mods = this.sortedModules;
      if (!mods.length) return 0;
      const done = mods.filter(m => m.module_status === 'completed').length;
      return Math.round((done / mods.length) * 100);
    },
    circleCircumference() {
      return 2 * Math.PI * 34; // r=34
    },
    circleOffset() {
      return this.circleCircumference * (1 - this.overallPct / 100);
    },
  },
  created() {
    this.fetchPathway();
  },
  methods: {
    fetchPathway() {
      this.loading = true;
      const id = this.$route.params.id;
      this.$http.get('glad/pathways/' + id)
        .then(resp => { this.pathway = resp.data; })
        .catch(() => { this.pathway = null; })
        .finally(() => { this.loading = false; });
    },
    enroll() {
      this.enrolling = true;
      this.$http.post('glad/pathways/' + this.pathway.id + '/enroll')
        .then(() => { this.fetchPathway(); })
        .catch(() => {})
        .finally(() => { this.enrolling = false; });
    },
    formatTrack(track) {
      const map = {
        foundations: 'Foundations',
        en_espanol: 'En Español',
        preschool: 'Preschool',
        secondary: 'Secondary',
      };
      return map[track] || track;
    },
    statusLabel(s) {
      const map = {
        not_enrolled: 'Not enrolled',
        enrolled: 'Enrolled',
        in_progress: 'In progress',
        completed: 'Completed',
      };
      return map[s] || s;
    },
    statusClass(s) {
      return {
        'glad-status-pill--neutral': s === 'not_enrolled',
        'glad-status-pill--info': s === 'enrolled',
        'glad-status-pill--active': s === 'in_progress',
        'glad-status-pill--success': s === 'completed',
      };
    },
    moduleStatusLabel(s) {
      const map = {
        locked: 'Locked',
        in_progress: 'In progress',
        completed: 'Complete',
      };
      return map[s] || s;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.pathway-detail-wrap { width: 100%; }
.pathway-detail-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
}
.glad-pathway-detail {
  padding: 1.5rem;
  font-family: var(--glad-font-sans);
}

.glad-loading {
  display: flex;
  justify-content: center;
  padding: 4rem 0;
}
.glad-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: glad-spin 0.7s linear infinite;
  display: block;
}
@keyframes glad-spin { to { transform: rotate(360deg); } }

.glad-breadcrumb {
  margin-bottom: 1.5rem;
}
.glad-breadcrumb__link {
  font-size: 0.85rem;
  color: var(--glad-text-soft);
  text-decoration: none;
  &:hover { color: var(--glad-ink); }
}

.glad-pathway-header {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--glad-line);
}
.glad-pathway-header__text { flex: 1; }
.glad-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  display: block;
  margin-bottom: 0.4rem;
}
.glad-pathway-header__title {
  font-family: var(--glad-font-serif);
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.4rem;
  line-height: 1.25;
}
.glad-pathway-header__sub {
  color: var(--glad-text-soft);
  font-size: 0.9rem;
  margin: 0 0 1rem;
}
.glad-pathway-header__meta {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1rem;
}
.glad-pathway-header__count {
  font-size: 0.82rem;
  color: var(--glad-text-muted);
}

.glad-status-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  &--neutral { background: var(--glad-paper-2); color: var(--glad-text-soft); }
  &--info    { background: #e0f0ff; color: #1d4ed8; }
  &--active  { background: #fef3c7; color: #92400e; }
  &--success { background: #dcfce7; color: #15803d; }
}

.glad-pathway-header__progress {
  position: relative;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
}
.glad-circle-progress {
  width: 80px;
  height: 80px;
}
.glad-circle-progress__pct {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--glad-ink);
}

.glad-pathway-description {
  font-size: 0.9rem;
  color: var(--glad-text-soft);
  line-height: 1.6;
  margin-bottom: 2rem;
}

.glad-modules__heading {
  font-family: var(--glad-font-serif);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 1rem;
}

.glad-module-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
  overflow: hidden;
}

.glad-module {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--glad-surface);
  border-bottom: 1px solid var(--glad-line);
  transition: background 0.12s ease;

  &:last-child { border-bottom: none; }

  &--locked {
    background: var(--glad-paper);
    opacity: 0.75;
  }
  &--active { background: #fffbf5; }
  &--completed { }
}

.glad-module__number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--glad-paper-2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--glad-ink);
  flex-shrink: 0;

  .glad-module--completed & { background: var(--glad-ink); color: #fff; }
  .glad-module--active    & { background: var(--glad-accent); color: #fff; }
  .glad-module--locked    & { background: var(--glad-paper-2); color: var(--glad-text-muted); }
}

.glad-module__content { flex: 1; }
.glad-module__title {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--glad-ink);
  margin: 0 0 0.2rem;
  .glad-module--locked & { color: var(--glad-text-soft); }
}
.glad-module__status {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--glad-text-muted);
  .glad-module--active    & { color: var(--glad-accent); }
  .glad-module--completed & { color: var(--glad-success); }
}
.glad-module__lock-hint {
  font-size: 0.75rem;
  color: var(--glad-text-muted);
  margin: 0.2rem 0 0;
}

.glad-module__action { flex-shrink: 0; }

.glad-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.25rem;
  background: var(--glad-ink);
  color: #fff;
  border: none;
  border-radius: var(--glad-radius);
  font-family: var(--glad-font-sans);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  &--sm { padding: 0.38rem 0.9rem; font-size: 0.8rem; }

  &--ghost {
    background: transparent;
    color: var(--glad-text-soft);
    border: 1.5px solid var(--glad-line);
    &:hover { background: var(--glad-paper-2); opacity: 1; }
  }
}

.glad-empty {
  text-align: center;
  padding: 4rem 0;
  color: var(--glad-text-soft);
}
</style>
